/**
 * Natural spawning, kept cheap: one attempt every few ticks, cheap checks first.
 *
 * Hostiles: random spot 24..48 blocks from the player, on an opaque floor with
 * room for the mob, not in liquid, effective light <= 7 (block light vs sky light
 * scaled by daylight), only in Ready chunks, capped near the player.
 * Animals: every Ready chunk near the player gets its initial herd once, seeded
 * by the chunk coordinates (deterministic); the "done" set forgets chunks that
 * unload so re-entering regenerates the same herd.
 */

import {
  collisionOf, IS_LAVA, FIRE_ID, GRASS_ID, IS_OPAQUE, IS_WATERY, MAGMA_ID, PODZOL_ID, SNOW_BLOCK_ID, SNOW_ID, SNOW_LAYER_ID,
  ICE_ID, MYCELIUM_ID, PACKED_ICE_ID, SAND_ID,
} from '../blocks';
import { WORLD_HEIGHT } from '../constants';
import { mulberry32 } from '../noise';
import { ChunkState, worldKey } from '../world/chunk';
import type { MobType } from '../game/types';
import { effectiveLight, type EntityHost, type PlayerTarget } from './host';

/** What spawning reads of a player (`host.player` has no id; server targets do). */
type Anchor = Omit<PlayerTarget, 'id'>;
import { horseHerd } from './riding/horse';
import { llamaHerd } from './trader/mobs';
import { extraHostile, herdVariant } from './overworld2/spawn';
import { overworld2Hooks } from './overworld2/mobs';
import { GROUP } from './overworld2/aquatic';
import { MOBS3_SIZE, dressHerdMob, mobs3Herd, mobs3Ocean } from './mobs3';
import { jungleHerd } from './overworld2/jungle';
import { foxHerd } from './overworld2/fox';
import { phantomSpawnCount } from './overworld2/phantom';

export const HOSTILE_CAP = 12;
export const ANIMAL_CAP = 24;
export const AMBIENT_CAP = 16;
/**
 * Server (several players): the caps above are per player, counting the mobs
 * within MOB_CAP_RANGE blocks of that player, under these world-wide caps
 * (vanilla: per-player mob caps under a global cap that grows with the
 * loaded area).
 */
export const MOB_CAP_RANGE = 128;
export const GLOBAL_HOSTILE_CAP = 70;
export const GLOBAL_ANIMAL_CAP = 200;
export const GLOBAL_AMBIENT_CAP = 60;
const MIN_SPAWN_DIST = 24;
const MAX_SPAWN_DIST = 48;
const HERD_RADIUS_CHUNKS = 4;
const HERD_CHANCE = 0.1;

const HOSTILE_TABLE: { type: MobType; weight: number; min: number; max: number }[] = [
  { type: 'zombie', weight: 100, min: 1, max: 4 },
  { type: 'skeleton', weight: 100, min: 1, max: 3 },
  { type: 'creeper', weight: 100, min: 1, max: 1 },
  { type: 'spider', weight: 100, min: 1, max: 1 },
  { type: 'enderman', weight: 10, min: 1, max: 4 },
];
const ANIMAL_TABLE: { type: MobType; weight: number }[] = [
  { type: 'pig', weight: 10 }, { type: 'sheep', weight: 12 },
  { type: 'cow', weight: 8 }, { type: 'chicken', weight: 10 },
];
const SIZE: Record<MobType, [number, number]> = {
  zombie: [0.6, 1.95], skeleton: [0.6, 1.99], creeper: [0.6, 1.7], spider: [1.4, 0.9],
  pig: [0.9, 0.9], cow: [0.9, 1.4], sheep: [0.9, 1.3], chicken: [0.4, 0.7],
  zombified_piglin: [0.6, 1.95], piglin: [0.6, 1.95], magma_cube: [2.04, 2.04],
  wither: [0.9, 3.5], ghast: [4, 4], blaze: [0.6, 1.8], wither_skeleton: [0.7, 2.4], hoglin: [1.4, 1.4], strider: [0.9, 1.7], enderman: [0.6, 2.9],
  villager: [0.6, 1.95], iron_golem: [1.4, 2.7], pillager: [0.6, 1.95], vindicator: [0.6, 1.95], zombie_villager: [0.6, 1.95],
  slime: [1.04, 1.04], cave_spider: [0.7, 0.5], husk: [0.6, 1.95], stray: [0.6, 1.99], drowned: [0.6, 1.95],
  horse: [1.4, 1.6], donkey: [1.4, 1.5], silverfish: [0.4, 0.3], endermite: [0.4, 0.3], wolf: [0.6, 0.85], witch: [0.6, 1.95], phantom: [0.9, 0.5], cat: [0.6, 0.7], squid: [0.95, 0.95], cod: [0.5, 0.3], salmon: [0.7, 0.4], bat: [0.5, 0.9],
  panda: [1.3, 1.25], parrot: [0.5, 0.9], fox: [0.6, 0.7], warden: [0.9, 2.9],
  wandering_trader: [0.6, 1.95], llama: [0.9, 1.87], trader_llama: [0.9, 1.87],
  ...MOBS3_SIZE,
};

export interface SpawnTarget {
  spawnMob(type: MobType, x: number, y: number, z: number): unknown;
  counts(): { hostile: number; animals: number; ambient?: number };
  /**
   * Dedicated server: the players to spawn around (dead ones and spectators
   * are skipped). Absent = `host.player` alone, capped by `counts()`.
   */
  anchors?: () => readonly Omit<PlayerTarget, 'id'>[];
  /** Counts within `r` blocks (horizontally) of a point: the per-player caps when there are `anchors`. */
  countsNear?(x: number, z: number, r: number): { hostile: number; animals: number; ambient?: number };
}

export class Spawner {
  private readonly herdDone = new Set<number>();
  private timer = 0;
  private herdTimer = 0;
  private phantomTimer = 1200;
  private ambientTimer = 100;

  /** The player this pass spawns around. */
  private p: Anchor;
  /** Every player spawning must keep its distance from (just `p` offline). */
  private all: readonly Anchor[] | null = null;

  constructor(private readonly host: EntityHost, private readonly target: SpawnTarget) { this.p = host.player; }

  /** Counts the caps are checked against: around `p` on a server, the whole world offline. */
  private counts(): { hostile: number; animals: number; ambient: number } {
    const t = this.target;
    if (!this.all || !t.countsNear) { const c = t.counts(); return { hostile: c.hostile, animals: c.animals, ambient: c.ambient ?? 0 }; }
    const c = t.countsNear(this.p.x, this.p.z, MOB_CAP_RANGE), g = t.counts();
    // a full world counts as full everywhere
    return {
      hostile: g.hostile >= GLOBAL_HOSTILE_CAP ? Infinity : c.hostile,
      animals: g.animals >= GLOBAL_ANIMAL_CAP ? Infinity : c.animals,
      ambient: (g.ambient ?? 0) >= GLOBAL_AMBIENT_CAP ? Infinity : c.ambient ?? 0,
    };
  }

  reset(): void {
    this.herdDone.clear();
    this.timer = 0;
    this.herdTimer = 0;
  }

  tick(): void {
    const anchors = this.target.anchors?.();
    if (!anchors) {
      this.p = this.host.player;
      this.all = null;
      if (!this.p.alive) return;
    }
    const hostile = ++this.timer >= 5;
    if (hostile) this.timer = 0;
    const ambient = --this.ambientTimer <= 0;
    if (ambient) this.ambientTimer = 40;
    const phantom = --this.phantomTimer <= 0;
    if (phantom) this.phantomTimer = 1200 + Math.floor(this.host.rnd() * 1200);
    const herd = ++this.herdTimer >= 10;
    if (herd) this.herdTimer = 0;
    if (!hostile && !ambient && !phantom && !herd) return;
    if (!anchors) { this.pass(hostile, ambient, phantom, herd); return; }
    this.all = anchors;
    if (herd) this.forgetHerds();
    for (const a of anchors) {
      if (!a.alive || a.spectator) continue;
      this.p = a;
      this.pass(hostile, ambient, phantom, herd);
    }
  }

  /** One player's share of this tick's spawn attempts. */
  private pass(hostile: boolean, ambient: boolean, phantom: boolean, herd: boolean): void {
    if (hostile) this.hostileAttempt();
    if (ambient) this.ambientAttempt();
    if (phantom) this.phantomAttempt();
    if (herd) { if (!this.all) this.forgetHerds(); this.herdScan(); }
  }

  // ------------------------------------------------------------ hostiles

  private isFree(x: number, y: number, z: number): boolean {
    const id = this.host.world.getBlock(x, y, z);
    return collisionOf(id).length === 0 && IS_WATERY[id] === 0 && IS_LAVA[id] === 0 && id !== FIRE_ID;
  }

  /** Does a mob of `type` fit standing at (x,y,z) on a solid floor? */
  private fits(type: MobType, x: number, y: number, z: number, requireOpaqueFloor: boolean): boolean {
    const w = this.host.world;
    if (y < 1 || y + 3 >= WORLD_HEIGHT) return false;
    const floor = w.getBlock(x, y - 1, z);
    if (requireOpaqueFloor ? IS_OPAQUE[floor] !== 1 : collisionOf(floor).length === 0) return false;
    if (floor === MAGMA_ID) return false;
    const h = Math.ceil(SIZE[type][1]);
    for (let k = 0; k < h; k++) if (!this.isFree(x, y + k, z)) return false;
    // wide mobs (spider) need the neighbours clear at body height too
    if (SIZE[type][0] > 1) {
      for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) if (!this.isFree(x + dx, y, z + dz)) return false;
    }
    return true;
  }

  private chunkReady(x: number, z: number): boolean {
    const c = this.host.world.getChunk(x >> 4, z >> 4);
    return !!c && c.state === ChunkState.Ready;
  }

  /** Find a floor near (x, yStart): scan down for air-over-solid. */
  private findFloor(type: MobType, x: number, yStart: number, z: number, opaque: boolean): number {
    const lo = Math.max(2, yStart - 24);
    for (let y = Math.min(yStart, WORLD_HEIGHT - 4); y >= lo; y--) {
      if (this.fits(type, x, y, z, opaque)) return y;
    }
    return -1;
  }

  private hostileAttempt(): void {
    const { hostile } = this.counts();
    if (hostile >= HOSTILE_CAP) return;
    const host = this.host, p = this.p, rnd = host.rnd;
    const ang = rnd() * Math.PI * 2;
    const dist = MIN_SPAWN_DIST + rnd() * (MAX_SPAWN_DIST - MIN_SPAWN_DIST);
    const x = Math.floor(p.x + Math.cos(ang) * dist);
    const z = Math.floor(p.z + Math.sin(ang) * dist);
    if (!this.chunkReady(x, z)) return;
    const yStart = Math.floor(p.y + (rnd() * 2 - 1) * 20);
    if (rnd() < 0.2 && this.waterAttempt(x, z, yStart)) return;

    // pick a type by weight
    let total = 0;
    for (const e of HOSTILE_TABLE) total += e.weight;
    let r = rnd() * total;
    let pick = HOSTILE_TABLE[0];
    for (const e of HOSTILE_TABLE) { if ((r -= e.weight) < 0) { pick = e; break; } }

    const y = this.findFloor(pick.type, x, yStart, z, true);
    if (y < 0) return;
    // slime chunks / swamps / deserts / cold biomes swap in their own mob
    let anyLight = false;
    const alt = extraHostile(rnd, pick.type, x, y, z, this.host.world.getSky(x, y, z),
      overworld2Hooks.biomeAt?.(x, z) ?? '');
    if (alt && this.fits(alt.type, x, y, z, true)) {
      pick = { weight: 0, ...alt } as typeof pick;
      anyLight = alt.anyLight;
    }
    if ((!anyLight && !this.darkEnough(x, y, z)) || !this.farEnough(x + 0.5, y, z + 0.5)) return;

    const n = pick.min + Math.floor(rnd() * (pick.max - pick.min + 1));
    let spawned = 0;
    for (let i = 0; i < n && hostile + spawned < HOSTILE_CAP; i++) {
      let sx = x, sz = z, sy = y;
      if (i > 0) {
        sx = x + Math.floor(rnd() * 7) - 3;
        sz = z + Math.floor(rnd() * 7) - 3;
        if (!this.chunkReady(sx, sz)) continue;
        sy = this.findFloor(pick.type, sx, y + 2, sz, true);
        if (sy < 0 || Math.abs(sy - y) > 3 || (!anyLight && !this.darkEnough(sx, sy, sz)) || !this.farEnough(sx + 0.5, sy, sz + 0.5)) continue;
      }
      this.target.spawnMob(pick.type, sx + 0.5, sy, sz + 0.5);
      spawned++;
    }
  }

  /** Squid / cod / salmon in oceans and rivers, bats in dark caves; capped separately (AMBIENT_CAP). */
  ambientAttempt(): void {
    const host = this.host, p = this.p, rnd = host.rnd, w = host.world;
    if (this.counts().ambient >= AMBIENT_CAP) return;
    const ang = rnd() * Math.PI * 2, dist = 12 + rnd() * 28;
    const x = Math.floor(p.x + Math.cos(ang) * dist), z = Math.floor(p.z + Math.sin(ang) * dist);
    if (!this.chunkReady(x, z)) return;
    const biome = overworld2Hooks.biomeAt?.(x, z) ?? '';
    const wet = biome.includes('ocean') || biome.includes('river');
    if (wet) {
      let y = Math.min(Math.floor(p.y) + 12, WORLD_HEIGHT - 4);
      while (y > 2 && IS_WATERY[w.getBlock(x, y, z)] === 0) y--;
      y -= 2;
      if (y < 30 || IS_WATERY[w.getBlock(x, y, z)] === 0 || IS_WATERY[w.getBlock(x, y + 1, z)] === 0) return;
      const cold = /frozen|cold|river/.test(biome);
      const roll = rnd();
      const type: MobType = mobs3Ocean(roll, biome) ?? (roll < 0.3 ? 'squid' : cold && roll < 0.75 ? 'salmon' : biome.includes('frozen') ? 'salmon' : 'cod');
      const g = type === 'dolphin' ? [1, 2] : GROUP[type as 'squid' | 'cod' | 'salmon'];
      const n = g[0] + Math.floor(rnd() * (g[1] - g[0] + 1));
      for (let i = 0; i < n; i++) {
        const sx = x + Math.floor(rnd() * 5) - 2, sz = z + Math.floor(rnd() * 5) - 2, sy = y + Math.floor(rnd() * 3) - 1;
        if (!this.chunkReady(sx, sz) || IS_WATERY[w.getBlock(sx, sy, sz)] === 0 || IS_WATERY[w.getBlock(sx, sy + 1, sz)] === 0) continue;
        this.target.spawnMob(type, sx + 0.5, sy, sz + 0.5);
      }
      return;
    }
    // bats: dark cave air well below sea level
    const y = Math.floor(p.y + (rnd() * 2 - 1) * 14);
    // axolotls: dark cave water below sea level (vanilla: lush caves; the caves here carry no biome of their own)
    if (y <= 54 && y >= 5 && IS_WATERY[w.getBlock(x, y, z)] === 1 && IS_WATERY[w.getBlock(x, y + 1, z)] === 1 && w.getSky(x, y + 1, z) === 0) {
      if (rnd() < 0.5 && this.farEnough(x + 0.5, y, z + 0.5)) { const n = 1 + Math.floor(rnd() * 3); for (let i = 0; i < n; i++) this.target.spawnMob('axolotl', x + 0.5, y, z + 0.5); }
      return;
    }
    if (y > 58 || y < 5 || rnd() > 0.25) return;
    if (!this.isFree(x, y, z) || !this.isFree(x, y + 1, z) || w.getBlockLight(x, y, z) > 3 || w.getSky(x, y, z) > 0) return;
    if (!this.farEnough(x + 0.5, y, z + 0.5) && rnd() < 0.5) return;
    const n = GROUP.bat[0] + Math.floor(rnd() * 3);
    for (let i = 0; i < n; i++) this.target.spawnMob('bat', x + 0.5, y, z + 0.5);
  }

  /** Vanilla PhantomSpawner: 20-34 blocks above a player who has not slept for 3+ days, at night, under open sky. */
  phantomAttempt(): void {
    const host = this.host, p = this.p, rnd = host.rnd;
    const n = phantomSpawnCount(p.restTicks ?? host.restTicks?.() ?? 0, host.isNight(), host.difficulty?.() ?? 'normal', rnd);
    if (n === 0 || p.y < 62) return;
    if (this.counts().hostile >= HOSTILE_CAP + 4) return;
    const y = Math.floor(p.y + 20 + rnd() * 14);
    if (y + 2 >= WORLD_HEIGHT) return;
    for (let i = 0; i < n; i++) {
      const x = Math.floor(p.x + rnd() * 20 - 10), z = Math.floor(p.z + rnd() * 20 - 10);
      if (!this.chunkReady(x, z) || !this.isFree(x, y, z) || host.world.getSky(x, y, z) < 15) continue;
      this.target.spawnMob('phantom', x + 0.5, y, z + 0.5);
    }
  }

  /** Drowned in oceans and rivers: a submerged cell (water above and below) in a dark enough spot. */
  private waterAttempt(x: number, z: number, yStart: number): boolean {
    const biome = overworld2Hooks.biomeAt?.(x, z) ?? '';
    if (!biome.includes('ocean') && !biome.includes('river')) return false;
    const w = this.host.world;
    let y = Math.min(yStart + 8, WORLD_HEIGHT - 4);
    while (y > 2 && IS_WATERY[w.getBlock(x, y, z)] === 0) y--;
    // y is the topmost water cell (or the floor); need two cells of water to stand in
    y -= 1;
    if (y < 2 || IS_WATERY[w.getBlock(x, y, z)] === 0 || IS_WATERY[w.getBlock(x, y + 1, z)] === 0) return false;
    if (!this.darkEnough(x, y, z) || !this.farEnough(x + 0.5, y, z + 0.5)) return false;
    // rivers are the common drowned habitat; open ocean spawns them rarely
    if (!biome.includes('river') && this.host.rnd() > 0.5) return true;
    this.target.spawnMob('drowned', x + 0.5, y, z + 0.5);
    return true;
  }

  private darkEnough(x: number, y: number, z: number): boolean {
    return effectiveLight(this.host, x, y, z) <= 7;
  }

  private farEnough(x: number, y: number, z: number): boolean {
    const p = this.p;
    const dx = x - p.x, dy = y - p.y, dz = z - p.z;
    if (dx * dx + dy * dy + dz * dz < MIN_SPAWN_DIST * MIN_SPAWN_DIST) return false;
    // nothing appears next to another player either
    if (this.all) {
      for (const q of this.all) {
        if (q === p || q.spectator) continue;
        if ((x - q.x) ** 2 + (y - q.y) ** 2 + (z - q.z) ** 2 < MIN_SPAWN_DIST * MIN_SPAWN_DIST) return false;
      }
    }
    return true;
  }

  // ------------------------------------------------------------- animals

  /** Forget the herds of unloaded chunks: coming back regenerates the same herd. */
  private forgetHerds(): void {
    const w = this.host.world;
    for (const k of this.herdDone) if (!w.chunks.has(k)) this.herdDone.delete(k);
  }

  private herdScan(): void {
    const host = this.host, w = host.world, p = this.p;
    const pcx = Math.floor(p.x) >> 4, pcz = Math.floor(p.z) >> 4;
    let budget = 2;
    for (let dz = -HERD_RADIUS_CHUNKS; dz <= HERD_RADIUS_CHUNKS && budget > 0; dz++) {
      for (let dx = -HERD_RADIUS_CHUNKS; dx <= HERD_RADIUS_CHUNKS && budget > 0; dx++) {
        const cx = pcx + dx, cz = pcz + dz;
        const key = worldKey(cx, cz);
        if (this.herdDone.has(key)) continue;
        const c = w.getChunk(cx, cz);
        if (!c || c.state !== ChunkState.Ready) continue;
        this.herdDone.add(key);
        budget--;
        this.spawnHerd(cx, cz);
      }
    }
  }

  private spawnHerd(cx: number, cz: number): void {
    if (this.counts().animals >= ANIMAL_CAP) return;
    const rnd = mulberry32(((cx * 73856093) ^ (cz * 19349663) ^ 0x5eed) >>> 0);
    if (rnd() >= HERD_CHANCE) return;
    if (this.spawnJungle(cx, cz) || this.spawnFoxes(cx, cz) || this.spawnMobs3(cx, cz)) return;
    let total = 0;
    for (const e of ANIMAL_TABLE) total += e.weight;
    let r = rnd() * total;
    let type = ANIMAL_TABLE[0].type;
    for (const e of ANIMAL_TABLE) { if ((r -= e.weight) < 0) { type = e.type; break; } }
    let count = 2 + Math.floor(rnd() * 3);
    const wolfRoll = rnd();
    // herd centre: a grass column somewhere in the chunk
    let hx = 0, hz = 0, hy = -1;
    for (let tries = 0; tries < 8 && hy < 0; tries++) {
      hx = cx * 16 + Math.floor(rnd() * 16);
      hz = cz * 16 + Math.floor(rnd() * 16);
      hy = this.grassTop(type, hx, hz);
    }
    if (hy < 0) return;
    // forests and taigas hold wolf packs of four instead
    if (herdVariant(wolfRoll, overworld2Hooks.biomeAt?.(hx, hz) ?? '')) {
      const wy = this.grassTop('wolf', hx, hz);
      if (wy >= 0) { type = 'wolf'; count = 4; hy = wy; }
    }
    // plains / savanna: herds of 2-6 horses (a few donkeys)
    const hh = horseHerd(mulberry32(((cx * 668265263) ^ (cz * 374761393) ^ 0x4015e) >>> 0)(), overworld2Hooks.biomeAt?.(hx, hz) ?? '');
    if (hh) {
      const horseY = this.grassTop(hh.type, hx, hz);
      if (horseY >= 0) { type = hh.type; count = hh.count; hy = horseY; }
    }
    // savanna / windswept hills: herds of 4-6 llamas (own rng stream)
    const lrnd = mulberry32(((cx * 2246822519) ^ (cz * 3266489917) ^ 0x11a3a) >>> 0);
    const lc = llamaHerd(lrnd(), lrnd(), overworld2Hooks.biomeAt?.(hx, hz) ?? '');
    if (lc > 0) {
      const lY = this.grassTop('llama', hx, hz);
      if (lY >= 0) { type = 'llama'; count = lc; hy = lY; }
    }
    let spawned = 0;
    const room = ANIMAL_CAP - this.counts().animals;
    for (let i = 0; i < count * 3 && spawned < count && spawned < room; i++) {
      const x = i === 0 ? hx : hx + Math.floor(rnd() * 7) - 3;
      const z = i === 0 ? hz : hz + Math.floor(rnd() * 7) - 3;
      const y = i === 0 ? hy : this.grassTop(type, x, z);
      if (y < 0) continue;
      this.target.spawnMob(type, x + 0.5, y, z + 0.5);
      spawned++;
    }
  }

  /**
   * Jungle chunks trade their herd for pandas or parrots (overworld2/jungle.ts). The canopy
   * hides the ground from `grassTop`, so parrots perch on whatever tops the column and pandas
   * get the forest floor under the leaves. Own RNG stream: the farm-animal rolls stay as they were.
   */
  private spawnJungle(cx: number, cz: number): boolean {
    const biome = overworld2Hooks.biomeAt?.(cx * 16 + 8, cz * 16 + 8) ?? '';
    if (!biome.includes('jungle')) return false;
    const rnd = mulberry32(((cx * 83492791) ^ (cz * 2971215073) ^ 0x1a9d1e) >>> 0);
    const pick = jungleHerd(rnd(), rnd(), biome);
    if (!pick) return false;
    const room = ANIMAL_CAP - this.counts().animals;
    let spawned = 0;
    for (let i = 0; i < 12 && spawned < pick.count && spawned < room; i++) {
      const x = cx * 16 + Math.floor(rnd() * 16), z = cz * 16 + Math.floor(rnd() * 16);
      const y = this.jungleSpot(pick.type, x, z);
      if (y < 0) continue;
      this.target.spawnMob(pick.type, x + 0.5, y, z + 0.5);
      spawned++;
    }
    return true;
  }

  /** Biome herds of the third roster (entities/mobs3): mooshrooms, rabbits, goats, polar bears, turtles, ocelots. Own RNG stream. */
  private spawnMobs3(cx: number, cz: number): boolean {
    const biome = overworld2Hooks.biomeAt?.(cx * 16 + 8, cz * 16 + 8) ?? '';
    const rnd = mulberry32(((cx * 1597334677) ^ (cz * 3812015801) ^ 0x30b53) >>> 0);
    const herd = mobs3Herd(rnd(), rnd(), biome);
    if (!herd) return false;
    const w = this.host.world;
    const hx = cx * 16 + 3 + Math.floor(rnd() * 10), hz = cz * 16 + 3 + Math.floor(rnd() * 10);
    const room = ANIMAL_CAP - this.counts().animals;
    let spawned = 0;
    for (let i = 0; i < herd.count * 4 && spawned < herd.count && spawned < room; i++) {
      const x = hx + Math.floor(rnd() * 7) - 3, z = hz + Math.floor(rnd() * 7) - 3;
      if (!this.chunkReady(x, z)) continue;
      let y = WORLD_HEIGHT - 4;
      while (y > 0 && collisionOf(w.getBlock(x, y, z)).length === 0 && IS_WATERY[w.getBlock(x, y, z)] === 0) y--;
      if (herd.type === 'ocelot') { y = this.jungleSpot('ocelot', x, z) - 1; if (y < 0) continue; }
      const layer = w.getBlock(x, y, z) === SNOW_LAYER_ID;
      const floor = w.getBlock(x, layer ? y - 1 : y, z);
      const f = herd.floor;
      const ok = f === 'mycelium' ? floor === MYCELIUM_ID
        : f === 'sand' ? floor === SAND_ID
        : f === 'snow' ? floor === GRASS_ID || floor === SNOW_ID || floor === SNOW_BLOCK_ID || floor === ICE_ID || floor === PACKED_ICE_ID
        : f === 'stone' ? IS_OPAQUE[floor] === 1
        : floor === GRASS_ID || floor === PODZOL_ID;
      if (!ok) continue;
      if (layer ? !this.isFree(x, y + 1, z) : !this.fits(herd.type, x, y + 1, z, false)) continue;
      const m = this.target.spawnMob(herd.type, x + 0.5, layer ? y + 0.2 : y + 1, z + 0.5);
      dressHerdMob(m, biome, rnd);
      spawned++;
    }
    return true;
  }

  /** Taiga chunks trade a third of their herds for a skulk of foxes (overworld2/fox.ts); snowy ground counts as floor. */
  private spawnFoxes(cx: number, cz: number): boolean {
    const biome = overworld2Hooks.biomeAt?.(cx * 16 + 8, cz * 16 + 8) ?? '';
    const rnd = mulberry32(((cx * 2654435761) ^ (cz * 40503) ^ 0xf0c5) >>> 0);
    const count = foxHerd(rnd(), rnd(), biome);
    if (count === 0) return false;
    const w = this.host.world;
    const hx = cx * 16 + 3 + Math.floor(rnd() * 10), hz = cz * 16 + 3 + Math.floor(rnd() * 10);
    const room = ANIMAL_CAP - this.counts().animals;
    let spawned = 0;
    for (let i = 0; i < count * 4 && spawned < count && spawned < room; i++) {
      const x = hx + Math.floor(rnd() * 7) - 3, z = hz + Math.floor(rnd() * 7) - 3;
      if (!this.chunkReady(x, z)) continue;
      let y = WORLD_HEIGHT - 4;
      while (y > 0 && collisionOf(w.getBlock(x, y, z)).length === 0 && IS_WATERY[w.getBlock(x, y, z)] === 0) y--;
      // a snow layer lies on the floor block: stand in its cell, a little above the snow
      const layer = w.getBlock(x, y, z) === SNOW_LAYER_ID;
      const floor = w.getBlock(x, layer ? y - 1 : y, z);
      if (floor !== GRASS_ID && floor !== SNOW_ID && floor !== PODZOL_ID && floor !== SNOW_BLOCK_ID) continue;
      if (layer ? !this.isFree(x, y + 1, z) : !this.fits('fox', x, y + 1, z, false)) continue;
      this.target.spawnMob('fox', x + 0.5, layer ? y + 0.2 : y + 1, z + 0.5);
      spawned++;
    }
    return true;
  }

  /** Feet y for a jungle mob in a column: parrots on the topmost block, pandas on grass / podzol below the canopy; -1 if none. */
  private jungleSpot(type: MobType, x: number, z: number): number {
    const w = this.host.world;
    if (!this.chunkReady(x, z)) return -1;
    let y = WORLD_HEIGHT - 4;
    while (y > 0 && collisionOf(w.getBlock(x, y, z)).length === 0 && IS_WATERY[w.getBlock(x, y, z)] === 0) y--;
    if (y <= 0 || IS_WATERY[w.getBlock(x, y, z)] === 1) return -1;
    if (type === 'parrot') return this.fits(type, x, y + 1, z, false) ? y + 1 : -1;
    for (const lo = Math.max(1, y - 48); y >= lo; y--) {
      const id = w.getBlock(x, y, z);
      if (IS_WATERY[id] === 1) return -1;
      if ((id === GRASS_ID || id === PODZOL_ID) && this.fits(type, x, y + 1, z, false)) return y + 1;
    }
    return -1;
  }

  /** Feet y of the grass surface in a column (grass block with sunlit room above), or -1. */
  private grassTop(type: MobType, x: number, z: number): number {
    const w = this.host.world;
    if (!this.chunkReady(x, z)) return -1;
    let y = WORLD_HEIGHT - 1;
    while (y > 0) {
      const id = w.getBlock(x, y, z);
      if (collisionOf(id).length > 0 || IS_WATERY[id] === 1) break;
      y--;
    }
    if (w.getBlock(x, y, z) !== GRASS_ID) return -1;
    if (!this.fits(type, x, y + 1, z, false)) return -1;
    if (w.getSky(x, y + 1, z) < 9) return -1;
    return y + 1;
  }
}
